<script setup>
import { useAlert } from 'dashboard/composables';
import { usePolicy } from 'dashboard/composables/usePolicy';
import AddCanned from './AddCanned.vue';
import EditCanned from './EditCanned.vue';
import { computed, onMounted, ref, defineOptions } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';
import { getUserPermissions } from 'dashboard/helper/permissionsHelper';
import { useAccount } from 'dashboard/composables/useAccount';
import {
  CANNED_RESPONSE_PUBLIC_PERMISSIONS,
  CANNED_RESPONSE_PRIVATE_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

import defaultArticleUrl from './assets/default_article.svg';
import privateArticleUrl from './assets/private_article.svg';
import robotArticleUrl from './assets/robot_article.svg';

defineOptions({
  name: 'CannedResponseSettings',
});

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { checkPermissions } = usePolicy();
const { accountId } = useAccount();

const currentUser = useMapGetter('getCurrentUser');
const currentRole = useMapGetter('getCurrentRole');

const currentUserPermissions = computed(() => {
  if (!currentUser.value || !accountId.value) return [];
  return getUserPermissions(currentUser.value, accountId.value);
});

const canCreateCannedResponse = computed(() =>
  checkPermissions([
    'administrator',
    CANNED_RESPONSE_PUBLIC_PERMISSIONS,
    CANNED_RESPONSE_PRIVATE_PERMISSIONS,
  ])
);

const showAddPopup = ref(false);
const loading = ref({});
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const activeResponse = ref({});
const cannedResponseAPI = ref({ message: '' });
const searchQuery = ref('');
const activeFilter = ref('all');
const accordionOpen = ref(true);
const collapsedGroups = ref({});

const sortOrder = ref('asc');
const records = computed(() =>
  getters.getSortedCannedResponses.value(sortOrder.value)
);
const uiFlags = computed(() => getters.getUIFlags.value);

const filteredRecords = computed(() => {
  let items = records.value;

  if (activeFilter.value === 'shared') {
    items = items.filter(i => i.visibility !== 'private_access');
  } else if (activeFilter.value === 'private') {
    items = items.filter(i => i.visibility === 'private_access');
  }

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    items = items.filter(
      item =>
        item.short_code.toLowerCase().includes(query) ||
        (item.content && item.content.toLowerCase().includes(query))
    );
  }
  return items;
});

const groupedRecords = computed(() => {
  const groups = {};
  filteredRecords.value.forEach(record => {
    const groupName = record.group_name || 'General';
    if (!groups[groupName]) groups[groupName] = [];
    groups[groupName].push(record);
  });
  const ordered = {};
  if (groups.General) ordered.General = groups.General;
  Object.keys(groups)
    .sort()
    .forEach(key => {
      if (key !== 'General') ordered[key] = groups[key];
    });
  return ordered;
});

const totalCount = computed(() => filteredRecords.value.length);

const deleteConfirmText = computed(
  () =>
    `${t('CANNED_MGMT.DELETE.CONFIRM.YES')} ${activeResponse.value.short_code}`
);

const deleteRejectText = computed(
  () =>
    `${t('CANNED_MGMT.DELETE.CONFIRM.NO')} ${activeResponse.value.short_code}`
);

const deleteMessage = computed(() => {
  return ` ${activeResponse.value.short_code} ? `;
});

const fetchCannedResponses = async () => {
  try {
    await store.dispatch('getCannedResponse');
  } catch (error) {
    // Ignore Error
  }
};

onMounted(() => {
  fetchCannedResponses();
});

const showAlertMessage = message => {
  loading.value[activeResponse.value.id] = false;
  activeResponse.value = {};
  cannedResponseAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  activeResponse.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  activeResponse.value = response;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteCannedResponse = async id => {
  try {
    await store.dispatch('deleteCannedResponse', id);
    showAlertMessage(t('CANNED_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('CANNED_MGMT.DELETE.API.ERROR_MESSAGE');
    showAlertMessage(errorMessage);
  }
};

const confirmDeletion = () => {
  loading.value[activeResponse.value.id] = true;
  closeDeletePopup();
  deleteCannedResponse(activeResponse.value.id);
};

const copyCannedResponse = async response => {
  try {
    await store.dispatch('createCannedResponse', {
      short_code: `${response.short_code}_copy`,
      content: response.content,
      visibility: response.visibility,
      group_name: response.group_name || 'General',
      suggest_tags: response.suggest_tags || [],
      media_attachment_signed_ids: (response.media_attachments || [])
        .map(media => media.signed_id)
        .filter(Boolean),
    });
    useAlert(t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('CANNED_MGMT.ADD.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const toggleAccordion = () => {
  accordionOpen.value = !accordionOpen.value;
};

const toggleGroup = groupName => {
  collapsedGroups.value[groupName] = !collapsedGroups.value[groupName];
};

const isGroupCollapsed = groupName => {
  return !!collapsedGroups.value[groupName];
};

const formatDate = item => {
  if (!item.created_at) return '';
  const d = new Date(item.created_at);
  const day = d.getDate();
  const month = d.toLocaleString('en', { month: 'long' });
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
};

const canModifyCannedResponse = cannedItem => {
  if (!currentUser.value || !cannedItem) return false;

  const userId = currentUser.value.id;
  const permissions = currentUserPermissions.value;

  if (currentRole.value === 'administrator') return true;

  if (
    permissions.includes('canned_response_manage') &&
    !permissions.includes('canned_response_public_manage') &&
    !permissions.includes('canned_response_private_manage')
  ) {
    return false;
  }

  if (cannedItem.visibility === 'private_access') {
    return (
      permissions.includes('canned_response_private_manage') &&
      cannedItem.user_id === userId
    );
  }

  return permissions.includes('canned_response_public_manage');
};
</script>

<template>
  <div class="lc-page">
    <!-- Header Bar -->
    <div class="lc-header">
      <div class="lc-header__left">
        <div class="lc-header__title">{{ $t('CANNED_MGMT.HEADER') }}</div>
      </div>
    </div>

    <!-- Scrollable Content -->
    <div class="lc-content">
      <div class="lc-content__inner">
        <!-- Accordion: How does it work? -->
        <div class="lc-accordion">
          <div class="lc-accordion__header">
            <div class="lc-accordion__title">
              {{ $t('CANNED_MGMT.HOW_IT_WORKS.TITLE') }}
            </div>
            <div class="lc-accordion__toggle" @click="toggleAccordion">
              {{
                accordionOpen
                  ? $t('CANNED_MGMT.HOW_IT_WORKS.HIDE')
                  : $t('CANNED_MGMT.HOW_IT_WORKS.SHOW')
              }}
              <span
                class="lc-accordion__chevron"
                :class="{ 'lc-accordion__chevron--down': !accordionOpen }"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    stroke="currentcolor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m6 15 6-6 6 6"
                  />
                </svg>
              </span>
            </div>
          </div>
          <div
            class="lc-accordion__body"
            :class="{ 'lc-accordion__body--collapsed': !accordionOpen }"
          >
            <div class="lc-carousel">
              <div class="lc-carousel__track">
                <div class="lc-carousel__card">
                  <div class="lc-carousel__card-inner">
                    <img
                      :src="defaultArticleUrl"
                      alt=""
                      class="lc-carousel__img"
                    />
                    <span>{{ $t('CANNED_MGMT.HOW_IT_WORKS.CARD_1') }}</span>
                  </div>
                </div>
                <div class="lc-carousel__card">
                  <div class="lc-carousel__card-inner">
                    <img
                      :src="privateArticleUrl"
                      alt=""
                      class="lc-carousel__img"
                    />
                    <span>{{ $t('CANNED_MGMT.HOW_IT_WORKS.CARD_2') }}</span>
                  </div>
                </div>
                <div class="lc-carousel__card">
                  <div class="lc-carousel__card-inner">
                    <img
                      :src="robotArticleUrl"
                      alt=""
                      class="lc-carousel__img"
                    />
                    <span>{{ $t('CANNED_MGMT.HOW_IT_WORKS.CARD_3') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Toolbar -->
        <div class="lc-toolbar">
          <div class="lc-toolbar__left">
            <!-- New Canned Response Button -->
            <button
              v-if="canCreateCannedResponse"
              class="lc-btn-primary"
              @click="openAddPopup"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 12h14m-7-7v14"
                />
              </svg>
              {{ $t('CANNED_MGMT.HEADER_BTN_TXT') }}
            </button>

            <!-- Spacer -->
            <div class="lc-toolbar__spacer" />

            <!-- Segmented Control -->
            <div class="lc-segmented">
              <button
                class="lc-segmented__btn"
                :class="{ 'lc-segmented__btn--active': activeFilter === 'all' }"
                @click="activeFilter = 'all'"
              >
                {{ $t('CANNED_MGMT.FILTER.ALL') }}
              </button>
              <button
                class="lc-segmented__btn"
                :class="{
                  'lc-segmented__btn--active': activeFilter === 'shared',
                }"
                @click="activeFilter = 'shared'"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    stroke="currentcolor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 7a4 4 0 1 0 8 0 4 4 0 0 0-8 0M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2M16 3.13a4 4 0 0 1 0 7.75M21 21v-2a4 4 0 0 0-3-3.85"
                  />
                </svg>
                {{ $t('CANNED_MGMT.FILTER.SHARED') }}
              </button>
              <button
                class="lc-segmented__btn"
                :class="{
                  'lc-segmented__btn--active': activeFilter === 'private',
                }"
                @click="activeFilter = 'private'"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    stroke="currentcolor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 7a4 4 0 1 0 8 0 4 4 0 0 0-8 0M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"
                  />
                </svg>
                {{ $t('CANNED_MGMT.FILTER.PRIVATE') }}
              </button>
            </div>
          </div>

          <!-- Search -->
          <div class="lc-search-wrap">
            <span class="lc-search-wrap__icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 10a7 7 0 1 0 14 0 7 7 0 0 0-14 0M21 21l-6-6"
                />
              </svg>
            </span>
            <input
              v-model="searchQuery"
              type="text"
              class="lc-search-wrap__input"
              :placeholder="$t('CANNED_MGMT.SEARCH_PLACEHOLDER')"
            />
          </div>
        </div>

        <!-- List Area -->
        <div class="lc-list-area">
          <!-- Loading State -->
          <div v-if="uiFlags.fetchingList" class="lc-loading">
            <woot-loading-state :message="$t('CANNED_MGMT.LOADING')" />
          </div>

          <!-- Empty State -->
          <div v-else-if="!filteredRecords.length" class="lc-empty">
            <p>{{ $t('CANNED_MGMT.LIST.404') }}</p>
          </div>

          <!-- Grouped Response List -->
          <template v-else>
            <div
              v-for="(items, groupName) in groupedRecords"
              :key="groupName"
              class="lc-group"
            >
              <!-- Group Header -->
              <div class="lc-group__header" @click="toggleGroup(groupName)">
                <div class="lc-group__label">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    class="lc-group__folder-icon"
                  >
                    <path
                      stroke="currentcolor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-6l-2-2H5a2 2 0 0 0-2 2z"
                    />
                  </svg>
                  {{ groupName }}
                  <span class="lc-group__count">{{ items.length }}</span>
                </div>
                <span
                  class="lc-group__chevron"
                  :class="{
                    'lc-group__chevron--collapsed': isGroupCollapsed(groupName),
                  }"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                  >
                    <path
                      stroke="currentcolor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="m6 15 6-6 6 6"
                    />
                  </svg>
                </span>
              </div>

              <!-- Group Items -->
              <div v-if="!isGroupCollapsed(groupName)" class="lc-group__items">
                <div
                  v-for="cannedItem in items"
                  :key="cannedItem.id"
                  class="lc-canned-item"
                >
                  <!-- Top row: tags + controls -->
                  <div class="lc-canned-item__top">
                    <div class="lc-canned-item__tags">
                      <span class="lc-canned-item__tag">{{
                        cannedItem.short_code
                      }}</span>
                      <span
                        v-for="tag in cannedItem.suggest_tags || []"
                        :key="`${cannedItem.id}-${tag}`"
                        class="lc-canned-item__tag lc-canned-item__tag--suggest"
                      >
                        {{ tag }}
                      </span>
                      <span
                        v-if="(cannedItem.media_attachments || []).length"
                        class="lc-canned-item__tag lc-canned-item__tag--media"
                      >
                        {{ (cannedItem.media_attachments || []).length }} media
                      </span>
                    </div>
                    <div
                      v-if="canModifyCannedResponse(cannedItem)"
                      class="lc-canned-item__controls"
                    >
                      <button
                        class="lc-canned-item__action"
                        :aria-label="$t('CANNED_MGMT.EDIT.BUTTON_TEXT')"
                        :title="$t('CANNED_MGMT.EDIT.BUTTON_TEXT')"
                        @click="openEditPopup(cannedItem)"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                        >
                          <path
                            fill="currentcolor"
                            d="M13.793 4.793a3.828 3.828 0 1 1 5.414 5.414l-.324.324a1 1 0 0 0-.176-.238l-5-5a1 1 0 0 0-.238-.176zM12.117 6.47l-8.824 8.823A1 1 0 0 0 3 16v4a1 1 0 0 0 1 1h4a1 1 0 0 0 .707-.293l8.824-8.824a1 1 0 0 1-.238-.176l-5-5a1 1 0 0 1-.176-.238"
                          />
                        </svg>
                      </button>
                      <button
                        class="lc-canned-item__action"
                        :aria-label="$t('CANNED_MGMT.COPY.BUTTON_TEXT')"
                        :title="$t('CANNED_MGMT.COPY.BUTTON_TEXT')"
                        @click="copyCannedResponse(cannedItem)"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                        >
                          <path
                            fill="currentcolor"
                            fill-rule="evenodd"
                            d="M6 3a3 3 0 0 0-3 3v8a3 3 0 0 0 3 3h1v1a3 3 0 0 0 3 3h8a3 3 0 0 0 3-3v-8a3 3 0 0 0-3-3h-1V6a3 3 0 0 0-3-3zm9 4V6a1 1 0 0 0-1-1H6a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h1v-5a3 3 0 0 1 3-3z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </button>
                      <button
                        class="lc-canned-item__action lc-canned-item__action--delete"
                        :aria-label="$t('CANNED_MGMT.DELETE.BUTTON_TEXT')"
                        :title="$t('CANNED_MGMT.DELETE.BUTTON_TEXT')"
                        @click="openDeletePopup(cannedItem)"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                        >
                          <path
                            fill="currentcolor"
                            d="M20 6a1 1 0 0 1 .117 1.993L20 8h-.081L19 19a3 3 0 0 1-2.824 2.995L16 22H8c-1.598 0-2.904-1.249-2.992-2.75l-.005-.167L4.08 8H4a1 1 0 0 1-.117-1.993L4 6zM14 2a2 2 0 0 1 2 2 1 1 0 0 1-1.993.117L14 4h-4l-.007.117A1 1 0 0 1 8 4a2 2 0 0 1 1.85-1.995L10 2z"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <!-- Content -->
                  <div class="lc-canned-item__content">
                    {{ cannedItem.content }}
                  </div>

                  <!-- Footer -->
                  <div class="lc-canned-item__footer">
                    <div class="lc-canned-item__meta">
                      <p class="lc-canned-item__added">
                        <span
                          class="lc-canned-item__visibility-badge"
                          :class="{
                            'lc-canned-item__visibility-badge--private':
                              cannedItem.visibility === 'private_access',
                          }"
                        >
                          {{
                            cannedItem.visibility === 'private_access'
                              ? $t('CANNED_MGMT.LIST.VISIBILITY.PRIVATE')
                              : $t('CANNED_MGMT.LIST.VISIBILITY.PUBLIC')
                          }}
                        </span>
                        <span class="lc-canned-item__date-text">
                          {{ formatDate(cannedItem) }}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <AddCanned v-if="showAddPopup" :on-close="hideAddPopup" />

    <EditCanned
      v-if="showEditPopup"
      :id="activeResponse.id"
      :edshort-code="activeResponse.short_code"
      :edcontent="activeResponse.content"
      :edvisibility="activeResponse.visibility"
      :edgroup-name="activeResponse.group_name"
      :edsuggest-tags="activeResponse.suggest_tags || []"
      :edmedia-attachments="activeResponse.media_attachments || []"
      :on-close="hideEditPopup"
    />

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('CANNED_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('CANNED_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </div>
</template>

<style scoped>
/* ===== LiveChat-Matching Canned Response Page Styles ===== */

.lc-page {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 100%;
  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
  font-family:
    Inter,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    'Helvetica Neue',
    Arial,
    sans-serif;
  background-color: #fff;
  color: #131317;
  overflow: hidden;
}

/* ---------- Header ---------- */
.lc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 56px;
  min-height: 56px;
  box-sizing: border-box;
  border-bottom: 1px solid #eeeeef;
  background-color: #fff;
  padding: 0 16px;
}

.lc-header__left {
  display: flex;
  align-items: center;
  height: 100%;
}

.lc-header__title {
  color: #131317;
  font-weight: 600;
  font-size: 18px;
  line-height: 56px;
  letter-spacing: 0.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-left: 8px;
}

/* ---------- Scrollable Content ---------- */
.lc-content {
  position: relative;
  overflow: auto;
  z-index: 0;
  height: 100%;
  box-sizing: border-box;
  padding: 32px 32px 120px;
}

.lc-content__inner {
  max-width: 100%;
  box-sizing: border-box;
}

/* ---------- Accordion ---------- */
.lc-accordion {
  margin: -5px 0 20px;
}

.lc-accordion__header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0;
  border-bottom: 1px solid #eeeeef;
}

.lc-accordion__title {
  font-size: 16px;
  font-weight: 600;
  color: #131317;
}

.lc-accordion__toggle {
  display: flex;
  align-items: center;
  font-size: 13px;
  cursor: pointer;
  color: #131317;
  user-select: none;
}

.lc-accordion__chevron {
  margin-left: 5px;
  display: flex;
  align-items: center;
  transition: transform 0.3s ease;
}

.lc-accordion__chevron--down {
  transform: rotate(180deg);
}

.lc-accordion__body {
  max-height: 500px;
  overflow: hidden;
  transition: max-height 0.6s ease-in-out;
}

.lc-accordion__body--collapsed {
  max-height: 0;
}

/* ---------- Carousel ---------- */
.lc-carousel {
  position: relative;
  width: 100%;
  display: flex;
}

.lc-carousel__track {
  display: flex;
  width: 100%;
  overflow-x: auto;
  align-items: flex-start;
  scrollbar-width: none;
  gap: 16px;
}

.lc-carousel__track::-webkit-scrollbar {
  display: none;
}

.lc-carousel__card {
  flex-grow: 0;
  flex-shrink: 0;
}

.lc-carousel__card-inner {
  display: flex;
  gap: 16px;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  height: 90px;
  max-width: 280px;
  padding: 16px;
  margin-top: 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  line-height: 1.42;
  border: 1px solid #eeeeef;
  background-color: #fff;
  color: #131317;
  transition: border-color 0.2s ease;
}

.lc-carousel__card-inner:hover {
  border-color: #d8d8da;
}

.lc-carousel__img {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
}

/* ---------- Toolbar ---------- */
.lc-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 32px;
  max-width: 100%;
}

.lc-toolbar__left {
  display: flex;
  flex-direction: column;
}

.lc-toolbar__spacer {
  height: 12px;
}

/* ---------- Primary Button ---------- */
.lc-btn-primary {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition-duration: 0.2s;
  transition-property: opacity, border, color, background-color, box-shadow;
  transition-timing-function: cubic-bezier(0.64, 0, 0.35, 1);
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  text-align: center;
  text-decoration: none;
  line-height: 20px;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 600;
  user-select: none;
  padding: 8px 16px;
  min-width: 36px;
  height: 36px;
  background-color: #0066ff;
  color: #fff;
  font-family: inherit;
  max-width: 260px;
}

.lc-btn-primary:hover {
  background-color: #0059e1;
}

/* ---------- Segmented Control ---------- */
.lc-segmented {
  display: inline-flex;
  flex-direction: row;
  border: 1px solid #eeeeef;
  border-radius: 8px;
  background-color: #fff;
  padding: 3px;
  vertical-align: middle;
  width: fit-content;
}

.lc-segmented__btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition-duration: 0.2s;
  transition-property: opacity, border, color, background-color, box-shadow;
  transition-timing-function: cubic-bezier(0.64, 0, 0.35, 1);
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  text-align: center;
  text-decoration: none;
  line-height: 20px;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 600;
  user-select: none;
  padding: 4px 16px;
  height: 28px;
  background-color: transparent;
  color: #131317;
  font-family: inherit;
}

.lc-segmented__btn + .lc-segmented__btn {
  margin-left: 5px;
}

.lc-segmented__btn--active {
  background-color: #eeeeef;
  color: #131317;
}

.lc-segmented__btn:hover:not(.lc-segmented__btn--active) {
  background-color: #f5f5f5;
}

/* ---------- Search ---------- */
.lc-search-wrap {
  align-self: flex-end;
  max-width: 250px;
  width: 100%;
  height: 36px;
  box-sizing: border-box;
  display: flex;
  position: relative;
  align-items: center;
  transition: all 0.25s ease-in-out;
  border: 1px solid #eeeeef;
  border-radius: 8px;
  background-color: #fff;
  overflow: hidden;
  color: #131317;
}

.lc-search-wrap:focus-within {
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
}

.lc-search-wrap__icon {
  position: absolute;
  left: 0;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  color: #8d8d95;
  pointer-events: none;
  z-index: 1;
}

.lc-search-wrap__input {
  border: 0;
  background-color: transparent;
  padding: 0 12px 0 36px;
  width: 100%;
  height: 100%;
  color: inherit;
  font-size: 14px;
  font-weight: 400;
  line-height: 36px;
  font-family: inherit;
  outline: none;
  text-overflow: ellipsis;
  box-sizing: border-box;
}

.lc-search-wrap__input::placeholder {
  color: #8d8d95;
}

/* ---------- List Area ---------- */
.lc-list-area {
  margin-top: 16px;
  position: relative;
  min-height: 200px;
  max-width: 100%;
}

.lc-loading,
.lc-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  color: #8d8d95;
}

/* ---------- Group ---------- */
.lc-group {
  margin-bottom: 1px;
}

.lc-group__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 28px;
  background-color: #d8d8da;
  color: #131317;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  border-radius: 4px;
  margin-bottom: 1px;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s ease;
}

.lc-group__header:hover {
  background-color: #d1d1d4;
}

.lc-group__label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.lc-group__folder-icon {
  color: #6b6b73;
  flex-shrink: 0;
}

.lc-group__count {
  font-weight: 400;
  font-size: 13px;
  color: #6b6b73;
  margin-left: 4px;
}

.lc-group__chevron {
  display: flex;
  align-items: center;
  color: #6b6b73;
  transition: transform 0.3s ease;
}

.lc-group__chevron--collapsed {
  transform: rotate(180deg);
}

/* ---------- Canned Item Card ---------- */
.lc-canned-item {
  position: relative;
  padding: 16px 18px 0;
  box-sizing: border-box;
  border-bottom: 1px solid #eeeeef;
  background-color: #fff;
  transition: background-color 0.15s ease;
}

.lc-canned-item:hover {
  background-color: #fafafa;
}

.lc-canned-item:hover .lc-canned-item__controls {
  visibility: visible;
}

.lc-canned-item__top {
  margin: 0 0 10px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.lc-canned-item__tags {
  margin-bottom: -8px;
  display: inline;
}

.lc-canned-item__tag {
  position: relative;
  display: inline-block;
  width: auto;
  max-width: 100%;
  overflow-wrap: break-word;
  padding: 5px 8px;
  margin: 0 8px 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: #131317;
  line-height: 22px;
  border: 1px solid #eeeeef;
  border-radius: 4px;
  text-shadow: none;
  background-color: #fff;
}

.lc-canned-item__tag::before {
  content: '#';
  font-size: 14px;
  line-height: 22px;
  font-weight: 600;
  color: #0066ff;
  margin-right: 4px;
}

.lc-canned-item__tag--suggest {
  border-color: #d6e4ff;
  background-color: #f3f7ff;
  color: #1e40af;
}

.lc-canned-item__tag--suggest::before {
  content: '🏷';
  color: inherit;
}

.lc-canned-item__tag--media {
  border-color: #dcfce7;
  background-color: #f0fdf4;
  color: #166534;
}

.lc-canned-item__tag--media::before {
  content: '🎬';
  color: inherit;
}

/* ---------- Item Controls ---------- */
.lc-canned-item__controls {
  visibility: hidden;
  display: flex;
  justify-content: space-between;
  gap: 4px;
}

.lc-canned-item__action {
  display: flex;
  cursor: pointer;
  background-color: transparent;
  border: none;
  outline: none;
  padding: 0;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 5px;
  color: #6b6b73;
  transition:
    color 0.15s ease,
    background-color 0.15s ease;
  font-family: inherit;
}

.lc-canned-item__action:hover {
  color: #131317;
  background-color: #f1f1f2;
}

.lc-canned-item__action--delete:hover {
  color: #d92d20;
  background-color: #fff5f5;
}

/* ---------- Item Content ---------- */
.lc-canned-item__content {
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #131317;
  margin: 11px 0 10px;
  white-space: pre-line;
  overflow-wrap: break-word;
}

/* ---------- Item Footer ---------- */
.lc-canned-item__footer {
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
  color: #131317;
  padding-bottom: 19px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-top: 20px;
  min-height: 24px;
  flex-wrap: wrap;
}

.lc-canned-item__meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.lc-canned-item__added {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b6b73;
  font-size: 13px;
}

.lc-canned-item__visibility-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 12px;
  background-color: #ecfdf5;
  color: #065f46;
  border: 1px solid #a7f3d0;
  line-height: 18px;
}

.lc-canned-item__visibility-badge--private {
  background-color: #f3f4f6;
  color: #374151;
  border-color: #e5e7eb;
}

.lc-canned-item__date-text {
  color: #8d8d95;
  font-size: 13px;
}

/* ---------- Group Items Container ---------- */
.lc-group__items {
  border: 1px solid #eeeeef;
  border-top: none;
  border-radius: 0 0 4px 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.lc-group__items .lc-canned-item:last-child {
  border-bottom: none;
}

/* ---------- Modal overrides ---------- */
:deep(.modal-mask .modal-container) {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
}
:deep(.modal-mask .modal-header),
:deep(.modal-mask .modal-content),
:deep(.modal-mask form) {
  background: #ffffff !important;
}
:deep(.modal-mask label) {
  color: #111827 !important;
}
:deep(.modal-mask input:not(.reset-base)),
:deep(.modal-mask select:not(.reset-base)),
:deep(.modal-mask textarea:not(.reset-base)) {
  background: #ffffff !important;
  border-color: #d1d5db !important;
  color: #111827 !important;
}
:deep(.variables-help),
:deep([class*='variable']) {
  background: #f9fafb !important;
  border-color: #e5e7eb !important;
}
:deep(.variables-help h3),
:deep(.variables-help p),
:deep(.variables-help span) {
  color: #111827 !important;
}
:deep(.variable-card),
:deep([class*='variable-card']) {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
}
:deep(.variable-card *) {
  color: #374151 !important;
}
:deep(.bg-n-solid-1),
:deep(.bg-n-solid-2),
:deep(.bg-n-solid-3),
:deep(.bg-n-background) {
  background: #ffffff !important;
}
:deep(.bg-n-alpha-1),
:deep(.bg-n-alpha-2),
:deep(.bg-n-alpha-3) {
  background: #f9fafb !important;
}
:deep(.border-n-slate-6),
:deep(.border-n-weak) {
  border-color: #e5e7eb !important;
}
:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
:deep(.text-n-slate-10) {
  color: #6b7280 !important;
}
</style>
